import React, { useMemo, useState } from 'react';
import { Card, Form, DatePicker, InputNumber, Input, Button, Space, Typography, Switch, Select, Table, Tag, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuthStore } from '../stores/authStore';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const { RangePicker } = DatePicker;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

/**
 * 中文函数注释：生成日期区间（包含端点），可选择是否跳过周末
 * @param {dayjs.Dayjs} start - 开始日期
 * @param {dayjs.Dayjs} end - 结束日期
 * @param {boolean} skipWeekend - 是否跳过周末
 * @returns {string[]} 日期字符串数组（YYYY-MM-DD）
 */
function genDates(start, end, skipWeekend) {
  const dates = [];
  let cursor = start.startOf('day');
  const last = end.startOf('day');
  while (cursor.isSame(last) || cursor.isBefore(last)) {
    const dow = cursor.day();
    if (!skipWeekend || (dow !== 0 && dow !== 6)) {
      dates.push(cursor.format('YYYY-MM-DD'));
    }
    cursor = cursor.add(1, 'day');
  }
  return dates;
}

/**
 * 中文函数注释：申请调休页面
 * 功能：
 * 1) 选择日期范围、每日调休时长、是否跳过周末、（可选）关联任务与原因；
 * 2) 一键提交为批量调休（/compensatory，leaveSlots）；
 * 3) 展示本人调休记录列表，支持删除待审批记录。
 */
export default function CompensatoryApplication() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [skipWeekend, setSkipWeekend] = useState(true);

  // 获取“我的任务”用于可选关联
  const { data: myTasksData, isLoading: tasksLoading } = useQuery(
    ['tasksMyForComp'],
    () => axios.get('/tasks/my').then(res => res.data.data),
    { refetchOnWindowFocus: false }
  );

  // 解析任务下拉（拍平成一维）
  const taskOptions = useMemo(() => {
    const groups = myTasksData?.tasksByProject || [];
    const all = [];
    groups.forEach(g => {
      (g.tasks || []).forEach(t => {
        all.push({ id: t.id, title: t.title, projectName: g.project?.name });
      });
    });
    return all;
  }, [myTasksData]);

  // 查询本人调休记录列表
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { data: leavesData, isLoading: leavesLoading } = useQuery(
    ['myCompLeaves', { userId: user?.id, page, limit: pageSize }],
    () => axios.get('/compensatory', {
      params: { userId: user?.id, page, limit: pageSize }
    }).then(res => res.data.data),
    { enabled: !!user?.id }
  );

  // 提交调休申请（批量 slots）
  const submitMutation = useMutation(
    async (payload) => {
      const res = await axios.post('/compensatory', payload);
      return res.data;
    },
    {
      onSuccess: (res) => {
        const createdCount = Array.isArray(res?.data) ? res.data.length : 1;
        message.success(`提交成功：已创建 ${createdCount} 条调休申请`);
        qc.invalidateQueries('myCompLeaves');
        form.resetFields(['reason']);
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || '提交失败，请重试');
      }
    }
  );

  /**
   * 中文函数注释：表单提交处理，生成 leaveSlots 并调用后端创建接口
   */
  const handleSubmit = async (values) => {
    const [start, end] = values.dateRange || [];
    if (!start || !end) {
      message.error('请选择日期范围');
      return;
    }

    const dailyHours = values.dailyHours;
    if (!dailyHours || dailyHours <= 0) {
      message.error('请输入有效的每日调休时长');
      return;
    }

    const dates = genDates(start, end, skipWeekend);
    if (dates.length === 0) {
      message.warning('日期区间内没有可用的工作日');
      return;
    }

    const leaveSlots = dates.map(d => ({ date: d, duration: dailyHours, reason: values.reason || undefined }));
    const payload = {
      leaveSlots
    };
    if (values.taskId) {
      payload.taskId = values.taskId;
    }

    submitMutation.mutate(payload);
  };

  // 删除待审批记录
  const deleteMutation = useMutation(
    (id) => axios.delete(`/compensatory/${id}`),
    {
      onSuccess: () => {
        message.success('已删除');
        qc.invalidateQueries('myCompLeaves');
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || '删除失败');
      }
    }
  );

  const columns = [
    { title: '日期', dataIndex: ['comp_date'], key: 'date', width: 140 },
    { title: '时长(小时)', dataIndex: ['duration'], key: 'duration', width: 120 },
    { title: '原因', dataIndex: ['reason'], key: 'reason', ellipsis: true },
    { 
      title: '状态', 
      dataIndex: ['status'], 
      key: 'status', 
      width: 120,
      render: (v) => {
        const map = { pending: 'processing', approved: 'success', rejected: 'error' };
        const text = { pending: '待审批', approved: '已批准', rejected: '已拒绝' };
        return <Tag color={map[v] || 'default'}>{text[v] || v}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (r) => r.status === 'pending' ? (
        <Popconfirm title="确认删除该申请？" onConfirm={() => deleteMutation.mutate(r.id)}>
          <Button size="small" danger loading={deleteMutation.isLoading}>删除</Button>
        </Popconfirm>
      ) : null
    }
  ];

  const dataSource = useMemo(() => {
    return (leavesData?.records || []).map(r => ({ ...r, key: r.id }));
  }, [leavesData]);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', maxWidth: 1200, margin: '0 auto' }}>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Title level={4} style={{ margin: 0 }}>申请调休</Title>
        </Space>
      </Card>

      <Card title="填写申请">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            dateRange: [dayjs(), dayjs()],
            dailyHours: 8,
            reason: '',
            taskId: undefined
          }}
          onFinish={handleSubmit}
        >
          <Form.Item label="日期范围" name="dateRange" rules={[{ required: true, message: '请选择日期范围' }]}> 
            <RangePicker style={{ width: '100%' }} allowClear={false} />
          </Form.Item>

          <Form.Item label="每日调休时长（小时）" name="dailyHours" rules={[{ required: true, message: '请输入每日调休时长' }]}> 
            <InputNumber min={0.5} max={12} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="是否跳过周末">
            <Switch checked={skipWeekend} onChange={setSkipWeekend} />
            <Text style={{ marginLeft: 8 }} type="secondary">开启后，将不为周六、周日创建调休记录</Text>
          </Form.Item>

          <Form.Item label="关联任务（可选）" name="taskId">
            <Select 
              allowClear 
              placeholder={tasksLoading ? '加载任务中...' : '可不选择'}
              loading={tasksLoading}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
            >
              {taskOptions.map(t => (
                <Option key={t.id} value={t.id}>{t.title}{t.projectName ? `（${t.projectName}）` : ''}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="原因说明（可选）" name="reason">
            <TextArea rows={3} placeholder="可补充说明，如：抵扣x月加班、个人安排等" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitMutation.isLoading}>提交申请</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="我的调休记录">
        <Table
          size="small"
          rowKey="id"
          loading={leavesLoading}
          dataSource={dataSource}
          columns={columns}
          pagination={{
            current: page,
            pageSize,
            total: leavesData?.pagination?.total || 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); }
          }}
        />
      </Card>
    </Space>
  );
}